import { html, LitElement, PropertyValues, unsafeCSS } from "lit";
import L from 'leaflet';
import { customElement, property } from "lit/decorators.js";
import ic_location from "/image/iconos/proximidad/location_on_modified.svg?raw";
import ic_home_pin from "/image/iconos/proximidad/home_pin.svg?raw";
import leafletStyles from 'leaflet/dist/leaflet.css?inline';
import styles from './RadarProximity.scss?inline';

import '../MapBase/MapBase';

@customElement('app-radar-proximity')
export class RadarProximity extends LitElement {
    static styles = [unsafeCSS(leafletStyles), unsafeCSS(styles)];

    @property({ type: Number }) k_schoolLatitud: number | null = null;
    @property({ type: Number }) k_schoolLongitud: number | null = null;
    @property({ type: Number }) k_latitude: number | null = 0;
    @property({ type: Number }) k_longitude: number | null = 0;
    @property({ type: Number }) k_radioLlegada: number = 50;
    @property({ type: Number }) k_radioZonaEscolar: number = 250;
    @property({ type: String }) k_radioLlegadaColor: string = 'var(--color_action1, #007bff)';
    @property({ type: String }) k_radioZonaEscolarColor: string = 'var(--color_app_pink1, #d30c7b)';

    private centerMarker!: L.Marker;
    private schoolMarker: L.Marker | null = null;
    private llegadaCircle!: L.Circle;
    private escolarCircle!: L.Circle;

    render() {
        return html`
            <div class='radar-container'>
                <app-map-base
                    .k_lat=${this.k_latitude}
                    .k_lng=${this.k_longitude}
                    .k_zoom=${17}
                    @map-ready=${this.onMapReady}
                >
                </app-map-base>
            </div>
        `
    }

    updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);
        if (this.centerMarker != null && (_changedProperties.has("k_latitude") || _changedProperties.has("k_longitude"))) {
            const newPos: L.LatLngExpression = [this.k_latitude, this.k_longitude];
            this.centerMarker.setLatLng(newPos);
            this.llegadaCircle.setLatLng(newPos);
            this.escolarCircle.setLatLng(newPos);
        }

        if (this.schoolMarker != null && (_changedProperties.has("k_schoolLatitud") || _changedProperties.has("k_schoolLongitud"))) {
            const schoolNewPos: L.LatLngExpression = [this.k_schoolLatitud, this.k_schoolLongitud];
            this.schoolMarker.setLatLng(schoolNewPos);
        }

        if (this.llegadaCircle && _changedProperties.has("k_radioLlegada")) {
            this.llegadaCircle.setRadius(this.k_radioLlegada);
        }

        if (this.escolarCircle && _changedProperties.has("k_radioZonaEscolar")) {
            this.escolarCircle.setRadius(this.k_radioZonaEscolar);
        }
    }

    onMapReady(event: CustomEvent) {
        const map = event.detail.map as L.Map;
        const center: L.LatLngExpression = [this.k_latitude, this.k_longitude];

        const schoolIcon = L.divIcon({
            className: 'school-static-icon',
            html: `<div class="school-position-marker">${ic_home_pin}</div>`,
            iconSize: [36, 36],
            iconAnchor: [18, 36]
        });

        if (this.k_schoolLatitud && this.k_schoolLongitud) {
            this.schoolMarker = L.marker([this.k_schoolLatitud, this.k_schoolLongitud], {
                icon: schoolIcon,
                interactive: false,
            }).addTo(map);
        }

        this.escolarCircle = L.circle(center, {
            radius: this.k_radioZonaEscolar,
            color: this.k_radioZonaEscolarColor,
            weight: 2,
            dashArray: '5, 10',
            fillColor: this.k_radioZonaEscolarColor,
            fillOpacity: 0.1
        }).addTo(map);

        this.llegadaCircle = L.circle(center, {
            radius: this.k_radioLlegada,
            color: this.k_radioLlegadaColor,
            weight: 2,
            fillColor: this.k_radioLlegadaColor,
            fillOpacity: 0.2,
            interactive: false,
        }).addTo(map);

        const googleSymbolIcon = L.divIcon({
            className: 'kidi-marker-wrapper',
            html: `<div class="kidi-center-marker">${ic_location}</div>`,
            iconSize: [36, 36],
            iconAnchor: [18, 36],
        });

        this.centerMarker = L.marker(center, {
            icon: googleSymbolIcon,
            draggable: true,
            zIndexOffset: 1000
        }).addTo(map);

        this.centerMarker.on('drag', () => {
            const newPos = this.centerMarker.getLatLng();
            this.llegadaCircle.setLatLng(newPos);
            this.escolarCircle.setLatLng(newPos);
        });

        this.centerMarker.on('dragend', () => {
            const newPos = this.centerMarker.getLatLng();
            this.dispatchEvent(new CustomEvent('location-changed', {
                detail: { lat: newPos.lat, lng: newPos.lng },
                bubbles: true,
                composed: true,
            }));
        });

        map.on('click', (e: L.LeafletMouseEvent) => {
            const clickedPos = e.latlng;

            this.centerMarker.setLatLng(clickedPos);
            this.llegadaCircle.setLatLng(clickedPos);
            this.escolarCircle.setLatLng(clickedPos);

            this.dispatchEvent(new CustomEvent('location-changed', {
                detail: { lat: clickedPos.lat, lng: clickedPos.lng },
                bubbles: true,
                composed: true,
            }));
        });
    }
}